import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/dateUtils";
import { usePager, Pager } from "@/components/ui/TablePager";
import {
  Activity, ArrowLeft, BarChart3, Clock, HardDrive, Loader2, ShieldCheck,
  Sparkles, Users, Video, Cloud, CheckCircle2,
} from "lucide-react";

const fmtDur = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${s}s`;
};

export default function AdminUsage() {
  const [data, setData] = useState(null);
  const [drive, setDrive] = useState(null);
  const [meet, setMeet] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [connectingMeet, setConnectingMeet] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("drive") === "connected") toast.success("Google Drive connected — recordings will now upload to your Drive");
    if (params.get("meet") === "connected") toast.success("Google Meet connected — classes will now get Meet links automatically");
  }, [params]);

  useEffect(() => {
    (async () => {
      try {
        const [u, d, ai, m] = await Promise.all([
          api.get("/admin/usage"), api.get("/admin/drive/status"), api.get("/admin/ai-usage"),
          api.get("/admin/meet/status"),
        ]);
        setData(u.data);
        setDrive(d.data);
        setAiUsage(ai.data);
        setMeet(m.data);
      } catch { toast.error("Could not load usage analytics"); }
    })();
  }, []);

  const connectMeet = async () => {
    setConnectingMeet(true);
    try {
      const { data: d } = await api.get("/admin/meet/connect");
      if (d.authorization_url) window.location.href = d.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Google Meet connect failed");
    } finally { setConnectingMeet(false); }
  };

  const connectDrive = async () => {
    setConnecting(true);
    try {
      const { data: d } = await api.get("/admin/drive/connect");
      if (d.authorization_url) window.location.href = d.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Drive connect failed");
    } finally { setConnecting(false); }
  };

  const teacherPager = usePager(data?.per_teacher || [], 15);
  const sessionsPager = usePager(
    [...(data?.per_user_sessions || [])].sort((a, b) => (b.last_seen || "").localeCompare(a.last_seen || "")), 15);

  if (!data) return <div className="text-neutral-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading usage analytics…</div>;
  const t = data.totals;
  const maxTrend = Math.max(1, ...data.trend.map((d) => d.classes));

  return (
    <div className="space-y-8" data-testid="admin-usage">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin" className="text-[11px] text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 mb-1"><ArrowLeft size={11} /> Admin dashboard</Link>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ admin · usage &amp; cost analytics</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter inline-flex items-center gap-2"><Activity size={26} /> System Usage</h1>
        </div>
        {/* Google Meet connection card — central platform account */}
        <div className="bg-white border border-black/8 rounded-xl p-4 min-w-[260px]" data-testid="meet-card">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1.5 inline-flex items-center gap-1.5"><Video size={11} /> Google Meet · live classes</div>
          {meet?.connected && !meet?.needs_reconnect ? (
            <div>
              <div className="text-sm font-semibold text-emerald-600 inline-flex items-center gap-1.5" data-testid="meet-connected"><CheckCircle2 size={14} /> Connected — Meet links auto-create</div>
              {meet?.last_cohost && !meet.last_cohost.ok && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mt-2" data-testid="meet-cohost-warning">
                  Teacher co-host add failed for <span className="font-mono">{meet.last_cohost.email}</span>: {meet.last_cohost.error || "unknown error"} — the Record button won't appear for this teacher.
                </div>
              )}
            </div>
          ) : meet?.connected && meet?.needs_reconnect ? (
            <button onClick={connectMeet} disabled={connectingMeet} data-testid="meet-reconnect-btn"
              className="text-xs font-bold bg-amber-500 text-white px-4 py-2 rounded-md hover:bg-amber-600 inline-flex items-center gap-1.5">
              {connectingMeet ? <Loader2 size={12} className="animate-spin" /> : <Video size={12} />} Re-connect — new Meet permission needed
            </button>
          ) : meet?.configured ? (
            <button onClick={connectMeet} disabled={connectingMeet} data-testid="meet-connect-btn"
              className="text-xs font-bold bg-[#00897B] text-white px-4 py-2 rounded-md hover:bg-[#00A48F] inline-flex items-center gap-1.5">
              {connectingMeet ? <Loader2 size={12} className="animate-spin" /> : <Video size={12} />} Connect the platform Google account
            </button>
          ) : (
            <div className="text-xs text-neutral-500">Add <code className="font-mono">GOOGLE_MEET_CLIENT_ID</code> / <code className="font-mono">GOOGLE_MEET_CLIENT_SECRET</code> to enable Meet links.</div>
          )}
        </div>
        {/* Google Drive connection card */}
        <div className="bg-white border border-black/8 rounded-xl p-4 min-w-[260px]" data-testid="drive-card">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1.5 inline-flex items-center gap-1.5"><Cloud size={11} /> Google Drive · recordings</div>
          {drive?.connected ? (
            <div className="text-sm font-semibold text-emerald-600 inline-flex items-center gap-1.5" data-testid="drive-connected"><CheckCircle2 size={14} /> Connected — recordings auto-upload</div>
          ) : drive?.configured ? (
            <button onClick={connectDrive} disabled={connecting} data-testid="drive-connect-btn"
              className="text-xs font-bold bg-[#0A0C10] text-white px-4 py-2 rounded-md hover:bg-black inline-flex items-center gap-1.5">
              {connecting ? <Loader2 size={12} className="animate-spin" /> : <Cloud size={12} />} Connect your Google Drive
            </button>
          ) : (
            <div className="text-xs text-neutral-500">Add <code className="font-mono">GOOGLE_CLIENT_ID</code> / <code className="font-mono">GOOGLE_CLIENT_SECRET</code> to enable Drive uploads.</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
        <Stat label="Total live session time" value={fmtDur(t.total_session_seconds)} icon={Clock} accent />
        <Stat label="Classes" value={t.classes} icon={Users} />
        <Stat label="Recordings" value={`${t.recordings} · ${t.recordings_mb} MB`} icon={Video} />
        <Stat label="AI generations" value={t.ai_generations} icon={Sparkles} />
        <Stat label="Teachers" value={data.roles.teacher} icon={ShieldCheck} />
        <Stat label="Students" value={data.roles.student} icon={Users} />
        <Stat label="Parents" value={data.roles.parent} icon={Users} />
        <Stat label="Quiz responses" value={t.quiz_responses} icon={BarChart3} />
      </div>

      {/* 14-day trend */}
      <div className="bg-white border border-black/8 rounded-xl p-5">
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-4">/ classes — last 14 days</div>
        <div className="flex items-end gap-1.5 h-28" data-testid="usage-trend">
          {data.trend.map((d) => (
            <div key={d.date} className="flex-1 h-full flex flex-col items-center justify-end gap-1" title={`${d.date} · ${d.classes} classes`}>
              <div className="w-full bg-[#0A0C10] rounded-t-sm min-h-[2px]" style={{ height: `${(d.classes / maxTrend) * 100}%` }} />
              <div className="text-[8px] font-mono text-neutral-400 rotate-0">{d.date.slice(8)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* AI usage breakdown */}
      {aiUsage && (
        <div data-testid="ai-usage-section">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ AI usage by feature</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200 mb-3">
            <Stat label="Total AI generations" value={aiUsage.totals.generations} icon={Sparkles} accent />
            <Stat label="Est. credits consumed" value={aiUsage.totals.est_credits} icon={BarChart3} />
            <Stat label="Cached images (credits saved)" value={aiUsage.totals.cached_images} icon={HardDrive} />
            <Stat label="Manual credit adjustments" value={aiUsage.totals.manual_credit_adjustments} icon={ShieldCheck} />
          </div>
          <div className="bg-white border border-black/8 rounded-xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm" data-testid="ai-usage-table">
              <thead className="bg-[#FBFAF7] text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">
                <tr>
                  <th className="px-4 py-3 text-left">Feature</th>
                  <th className="px-4 py-3 text-right">Generated</th>
                  <th className="px-4 py-3 text-right">Cost / gen</th>
                  <th className="px-4 py-3 text-right">Est. credits</th>
                </tr>
              </thead>
              <tbody>
                {aiUsage.rows.map((r) => (
                  <tr key={r.key} className="border-t border-neutral-100 hover:bg-neutral-50">
                    <td className="px-4 py-2.5 font-semibold">{r.label}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.count}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-neutral-500">{r.cost_per}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold">{r.est_credits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 text-right">
            <Link to="/admin/packages" className="text-[11px] text-neutral-500 hover:text-neutral-900 underline">Adjust credit costs →</Link>
          </div>
        </div>
      )}

      {/* Per-teacher usage */}
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ per-teacher usage</div>
        <div className="bg-white border border-black/8 rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid="teacher-usage-table">
            <thead className="bg-[#FBFAF7] text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">
              <tr>
                <th className="px-4 py-3 text-left">Teacher</th>
                <th className="px-4 py-3 text-right">Classes</th>
                <th className="px-4 py-3 text-right">Students taught</th>
                <th className="px-4 py-3 text-right">Live time (all users)</th>
                <th className="px-4 py-3 text-right">Scheduled min</th>
                <th className="px-4 py-3 text-right">Recordings</th>
                <th className="px-4 py-3 text-right">AI gens</th>
                <th className="px-4 py-3 text-right">Credits left</th>
              </tr>
            </thead>
            <tbody>
              {teacherPager.pageItems.map((r) => (
                <tr key={r.teacher_id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`teacher-usage-${r.teacher_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-[11px] text-neutral-500">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{r.classes}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.unique_students}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{fmtDur(r.live_session_seconds)}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.scheduled_minutes}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.recordings}{r.recordings_mb ? ` · ${r.recordings_mb}MB` : ""}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.ai_generations}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.ai_credits_left ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={teacherPager.page} pages={teacherPager.pages} total={teacherPager.total} onPage={teacherPager.setPage} testid="teacher-usage-pager" />
        </div>
      </div>

      {/* Per-user session time */}
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ per-user live session time (top 200)</div>
        <div className="bg-white border border-black/8 rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid="user-sessions-table">
            <thead className="bg-[#FBFAF7] text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">
              <tr>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-right">Total session time</th>
                <th className="px-4 py-3 text-right">Classes joined</th>
                <th className="px-4 py-3 text-right">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {sessionsPager.pageItems.map((u) => (
                <tr key={u.user_id} className="border-t border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-3 font-semibold">{u.name || u.user_id}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${u.role === "teacher" ? "bg-violet-100 text-violet-700" : u.is_guest ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`}>{u.is_guest ? "guest" : u.role}</span></td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{fmtDur(u.total_seconds)}</td>
                  <td className="px-4 py-3 text-right font-mono">{u.classes_joined}</td>
                  <td className="px-4 py-3 text-right font-mono text-[11px] text-neutral-500">{fmtDateTime(u.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={sessionsPager.page} pages={sessionsPager.pages} total={sessionsPager.total} onPage={sessionsPager.setPage} testid="user-sessions-pager" />
          {data.per_user_sessions.length === 0 && (
            <div className="p-8 text-center text-xs text-neutral-500 inline-flex items-center gap-2 w-full justify-center"><HardDrive size={13} /> Session-time tracking starts with the next live class heartbeats.</div>
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, accent }) => (
  <div className={`p-4 ${accent ? "bg-[#0A0C10] text-white" : "bg-white"}`}>
    <div className={`text-[10px] font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1.5 ${accent ? "text-[#FFD600]" : "text-neutral-500"}`}>
      <Icon size={11} /> {label}
    </div>
    <div className="text-2xl font-display font-bold mt-1">{value}</div>
  </div>
);
